import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Typography, 
  Box, 
  Paper, 
  CircularProgress, 
  Chip,
  Divider,
  Grid,
  Card,
  CardContent,
  Button,
  LinearProgress
} from '@material-ui/core';
import { 
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Computer as ComputerIcon,
  TrendingUp as TrendingUpIcon
} from '@material-ui/icons';
import firebase from "firebase/app";
import "firebase/database";
import moment from 'moment';
import AIEvaluationService from '../services/AIEvaluationService';
import swal from 'sweetalert';
import TopMenu from './TopMenu';

const SubmissionResult = () => {
  const { examCode, submissionId } = useParams();
  const [submission, setSubmission] = useState(null);
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aiEvaluation, setAiEvaluation] = useState(null);
  const [evaluating, setEvaluating] = useState(false);
  const [score, setScore] = useState(0);
  const [maxScore, setMaxScore] = useState(0);
  const [scorePercentage, setScorePercentage] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        
        // Fetch submission data
        const submissionRef = firebase.database().ref(`Exam_results/${examCode}/${submissionId}`);
        const submissionSnapshot = await submissionRef.once('value');
        const submissionData = submissionSnapshot.val();


        if (!submissionData || !submissionData.isDynamic) {
          setError('Dynamic assessment submission not found');
          setLoading(false);
          return;
        }

        // If answers are stored separately, fetch them
        let answers = submissionData.answers;
        if (submissionData.hasSeparateAnswers) {
          const answersRef = firebase.database().ref(`Exam_results/${examCode}/${submissionId}/answers`);
          const answersSnapshot = await answersRef.once('value');
          answers = answersSnapshot.val() || {};
        }

        // Fetch content from storage for storage references
        const processedAnswers = {};
        for (const [questionId, answer] of Object.entries(answers)) {
          if (answer && typeof answer === 'object' && answer.type === 'storage_reference') {
            try {
              // Fetch content from Firebase Storage
              const response = await fetch(answer.url);
              const content = await response.text();
              processedAnswers[questionId] = content;
            } catch (error) {
              processedAnswers[questionId] = `[Error loading content: ${error.message}]`;
            }
          } else {
            processedAnswers[questionId] = answer;
          }
        }


        setSubmission({
          ...submissionData,
          answers: processedAnswers,
          // Add student info for compatibility
          student: {
            name: submissionData.email || 'Unknown',
            email: submissionData.email || 'No email'
          }
        });

        // Check if AI evaluation already exists
        if (submissionData.aiEvaluation) {
          setAiEvaluation(submissionData.aiEvaluation);
        }

        // Fetch exam data
        const examRef = firebase.database().ref(`TestPapers/${examCode}`);
        const examSnapshot = await examRef.once('value');
        const examData = examSnapshot.val();


        if (!examData || !examData.isDynamic) {
          setError('Dynamic assessment not found');
          setLoading(false);
          return;
        }

        setExam(examData);

        if (submissionData) {
          let displayedScore = 0;
          let displayedMaxScore = 0;
          
          // If the full AI evaluation is present, use that as the source of truth
          if (submissionData.aiEvaluation) {
            displayedScore = submissionData.aiEvaluation.totalScore;
            displayedMaxScore = submissionData.aiEvaluation.maxPossibleScore;
          } 
          // Otherwise, fall back to the provisional MCQ score
          else if (submissionData.provisionalScore) {
            displayedScore = submissionData.provisionalScore.score;
            displayedMaxScore = submissionData.provisionalScore.maxScore;
          }

          setScore(displayedScore);
          setMaxScore(displayedMaxScore);

          const percentage = displayedMaxScore > 0 ? (displayedScore / displayedMaxScore) * 100 : 0;
          setScorePercentage(percentage);
        }

        setLoading(false);
      } catch (err) {
        setError('Failed to load submission data');
        setLoading(false);
      }
    };

    fetchData();
  }, [examCode, submissionId]);

  const handleAIEvaluation = async () => {
    if (!exam || !submission) return;

    setEvaluating(true);
    try {
      const evaluation = await AIEvaluationService.evaluateSubmission(exam, submission);
      
      // Save evaluation to Firebase using the correct path
      const submissionRef = firebase.database().ref(`Exam_results/${examCode}/${submissionId}`);
      await submissionRef.update({
        aiEvaluation: evaluation
      });

      setAiEvaluation(evaluation);
      swal("Success", "AI evaluation completed successfully!", "success");
    } catch (error) {
      swal("Error", "AI evaluation failed. Please try again.", "error");
    } finally {
      setEvaluating(false);
    }
  };

  const renderVideoAnalysis = (analysisData) => {
    if (analysisData.error) {
      return (
        <Box p={2} bgcolor="#ffebee" borderRadius={4}>
          <Typography variant="body2" color="error">
            Analysis Error: {analysisData.error}
          </Typography>
        </Box>
      );
    }

    const scores = [
      { label: 'Fluency', score: analysisData.fluencyScore, color: '#1976d2' },
      { label: 'Grammar', score: analysisData.grammarScore, color: '#388e3c' },
      { label: 'Pronunciation', score: analysisData.pronunciationScore, color: '#f57c00' },
      { label: 'MTI', score: analysisData.mtiScore, color: '#7b1fa2' }
    ];

    return (
      <Box>
        {/* Overall Score */}
        <Box mb={2} p={2} bgcolor="#e3f2fd" borderRadius={4}>
          <Typography variant="h6" color="primary" gutterBottom>
            Overall Score: {analysisData.finalScore}/20
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {analysisData.remark}
          </Typography>
        </Box>

        {/* Individual Scores */}
        <Grid container spacing={2} mb={2}>
          {scores.map((scoreItem, index) => (
            <Grid item xs={6} sm={3} key={index}>
              <Card style={{ backgroundColor: scoreItem.color, color: 'white' }}>
                <CardContent style={{ padding: 8, textAlign: 'center' }}>
                  <Typography variant="h6">{scoreItem.score}</Typography>
                  <Typography variant="caption">{scoreItem.label}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Transcript */}
        {analysisData.transcript && (
          <Box mb={2}>
            <Typography variant="subtitle2" gutterBottom>
              Transcript:
            </Typography>
            <Paper variant="outlined" p={2} style={{ backgroundColor: '#fafafa' }}>
              <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                {analysisData.transcript}
              </Typography>
            </Paper>
          </Box>
        )}

        {/* Processing Info */}
        <Box mt={2}>
          <Typography variant="caption" color="textSecondary">
            Processing Time: {analysisData.processingTime}s | 
            Analyzed: {new Date(analysisData.analysisTimestamp).toLocaleString()}
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderAnswer = (question, answer) => {
    if (!answer) {
      return <Typography color="textSecondary" fontStyle="italic">No answer provided</Typography>;
    }

    switch (question.type) {
      case 'mcq-single':
      case 'mcq-multi':
        if (Array.isArray(answer)) {
          return (
            <Box>
              {answer.map((selectedOption, index) => {
                const option = question.options.find(opt => opt.id === selectedOption);
                return (
                  <Chip
                    key={index}
                    label={option ? option.text : selectedOption}
                    color="primary"
                    variant="outlined"
                    style={{ margin: 4 }}
                  />
                );
              })}
            </Box>
          );
        } else {
          const option = question.options.find(opt => opt.id === answer);
          return (
            <Chip
              label={option ? option.text : answer}
              color="primary"
              variant="outlined"
            />
          );
        }

      case 'text':
        return (
          <Paper variant="outlined" style={{ padding: 16, backgroundColor: '#f5f5f5' }}>
            <Typography>{answer}</Typography>
          </Paper>
        );

      case 'code':
        return (
          <Paper variant="outlined" style={{ padding: 16, backgroundColor: '#1e1e1e', color: '#fff' }}>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{answer}</pre>
          </Paper>
        );

      case 'upload':
        return (
          <Box>
            <Typography variant="body2" color="primary">
              <a href={answer} target="_blank" rel="noopener noreferrer">
                View File
              </a>
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {answer}
            </Typography>
          </Box>
        );

      case 'video':
        return (
          <Box>
            {/* Selected Scenario */}
            {answer && answer.selectedScenario && (
              <Box mb={2} p={2} bgcolor="#e8f5e9" borderRadius={4}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Selected Scenario:
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {answer.selectedScenario}
                </Typography>
              </Box>
            )}

            {/* Video Link */}
            {(answer && answer.videoUrl) || (answer && answer.videoPath) ? (
              <Box mb={2}>
                <Box display="flex" gap={2} alignItems="center" mb={1}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<span>🎥</span>}
                    onClick={async () => {
                      try {
                        let videoUrl = answer.videoUrl;
                        
                        if (!videoUrl && answer.videoPath) {
                          const fullPath = `submissions/${answer.videoPath}`;
                          const storageRef = firebase.storage().ref(fullPath);
                          videoUrl = await storageRef.getDownloadURL();
                        }
                        
                        if (videoUrl) {
                          // Open video in a new tab
                          window.open(videoUrl, '_blank');
                        } else {
                          alert('Video not found or unavailable');
                        }
                      } catch (error) {
                        console.error('Error loading video:', error);
                        alert('Error loading video. Please try again.');
                      }
                    }}
                    style={{
                      background: '#9c27b0',
                      color: '#fff',
                      fontWeight: 600,
                      textTransform: 'none'
                    }}
                  >
                    Play Video
                  </Button>
                  
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={async () => {
                      try {
                        let videoUrl = answer.videoUrl;
                        
                        if (!videoUrl && answer.videoPath) {
                          const fullPath = `submissions/${answer.videoPath}`;
                          const storageRef = firebase.storage().ref(fullPath);
                          videoUrl = await storageRef.getDownloadURL();
                        }
                        
                        if (videoUrl) {
                          // Copy URL to clipboard
                          await navigator.clipboard.writeText(videoUrl);
                          alert('Video URL copied to clipboard!');
                        }
                      } catch (error) {
                        console.error('Error copying video URL:', error);
                        alert('Error copying video URL. Please try again.');
                      }
                    }}
                    style={{
                      borderColor: '#9c27b0',
                      color: '#9c27b0',
                      fontWeight: 600,
                      textTransform: 'none'
                    }}
                  >
                    Copy URL
                  </Button>
                </Box>
                
                <Typography variant="caption" color="textSecondary">
                  Video: {answer.videoUrl || answer.videoPath}
                </Typography>
              </Box>
            ) : null}

            {/* Video Analysis Results */}
            {submission?.videoAnalysis?.[question.id] && (
              <Box mt={2} p={2} bgcolor="#f8f9fa" borderRadius={4}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Video Analysis Results
                </Typography>
                {renderVideoAnalysis(submission.videoAnalysis[question.id])}
              </Box>
            )}
          </Box>
        );

      default:
        return <Typography>{String(answer)}</Typography>;
    }
  };

  const renderAIEvaluation = (questionId) => {
    if (!aiEvaluation || !aiEvaluation.evaluations[questionId]) {
      return null;
    }

    const evaluation = aiEvaluation.evaluations[questionId];
    const percentage = evaluation.maxScore > 0 ? (evaluation.score / evaluation.maxScore) * 100 : 0;

    return (
      <Box mt={2} p={2} bgcolor="#f8f9fa" borderRadius={4}>
        <Box display="flex" alignItems="center" mb={1}>
          <ComputerIcon style={{ marginRight: 8, color: '#007bff' }} />
          <Typography variant="subtitle2" color="primary">
            AI Evaluation
          </Typography>
          <Chip 
            label={`${evaluation.confidence}% confidence`} 
            size="small" 
            color={evaluation.confidence > 80 ? "primary" : "secondary"}
            style={{ marginLeft: 8 }}
          />
        </Box>
        
        <Box display="flex" alignItems="center" mb={1}>
          <Typography variant="h6" color="primary" style={{ marginRight: 8 }}>
            {evaluation.score}/{evaluation.maxScore}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            ({Math.round(percentage)}%)
          </Typography>
        </Box>
        
        <LinearProgress 
          variant="determinate" 
          value={percentage} 
          style={{ marginBottom: 8 }}
          color={percentage >= 80 ? "primary" : percentage >= 60 ? "secondary" : "error"}
        />
        
        <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
          {evaluation.feedback}
        </Typography>
        
        {evaluation.evaluationType === 'text' && evaluation.wordCount && (
          <Typography variant="caption" color="textSecondary" display="block" mt={1}>
            Word count: {evaluation.wordCount} | Keywords found: {evaluation.keywordsFound}/{evaluation.totalKeywords}
          </Typography>
        )}
        
        {evaluation.evaluationType === 'code' && (
          <Typography variant="caption" color="textSecondary" display="block" mt={1}>
            Code lines: {evaluation.codeLines} | 
            Function: {evaluation.hasFunction ? '✅' : '❌'} | 
            Loop: {evaluation.hasLoop ? '✅' : '❌'} | 
            Conditional: {evaluation.hasConditional ? '✅' : '❌'}
          </Typography>
        )}
      </Box>
    );
  };

  const calculateScore = () => {
    if (!submission || !exam) return { total: 0, maxPossible: 0, percentage: 0 };

    // Use AI evaluation if available, otherwise fall back to basic calculation
    if (aiEvaluation) {
      return {
        total: aiEvaluation.totalScore,
        maxPossible: aiEvaluation.maxPossibleScore,
        percentage: aiEvaluation.overallPercentage
      };
    }

    // Fallback to basic MCQ-only calculation
    let totalScore = 0;
    let maxPossibleScore = 0;

    exam.sections.forEach(section => {
      section.questions.forEach(question => {
        const answer = submission.answers[question.id];
        
        if (question.type === 'mcq-single' || question.type === 'mcq-multi') {
          if (Array.isArray(answer)) {
            answer.forEach(selectedOptionId => {
              const option = question.options.find(opt => opt.id === selectedOptionId);
              if (option && option.isCorrect) {
                totalScore += Number(option.marks) || 1;
              }
            });
          } else if (answer) {
            const option = question.options.find(opt => opt.id === answer);
            if (option && option.isCorrect) {
              totalScore += Number(option.marks) || 1;
            }
          }
          
          const correctOptions = question.options.filter(opt => opt.isCorrect);
          const maxQuestionScore = correctOptions.reduce((sum, opt) => sum + (Number(opt.marks) || 1), 0);
          maxPossibleScore += maxQuestionScore;
        }
      });
    });

    const percentage = maxPossibleScore > 0 ? (totalScore / maxPossibleScore) * 100 : 0;

    return {
      total: totalScore,
      maxPossible: maxPossibleScore,
      percentage: Math.round(percentage * 100) / 100
    };
  };

  const getEvaluationDetails = (questionId) => {
    if (submission?.aiEvaluation?.evaluations?.[questionId]) {
      return submission.aiEvaluation.evaluations[questionId];
    }
    return null; // No AI evaluation for this question (e.g., it was an MCQ)
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography color="error" variant="h6">{error}</Typography>
      </Box>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <Paper elevation={2} style={{ padding: 24, marginBottom: 24 }}>
        <Typography variant="h4" gutterBottom>{exam.examTitle}</Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box display="flex" alignItems="center" mb={1}>
              <PersonIcon style={{ marginRight: 8 }} />
              <Typography variant="h6">
                {submission.student.name || 'Unknown Student'}
              </Typography>
            </Box>
            <Typography color="textSecondary">
              {submission.student.email || 'No email provided'}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box display="flex" alignItems="center" mb={1}>
              <ScheduleIcon style={{ marginRight: 8 }} />
              <Typography variant="h6">
                Submitted: {moment(submission.submittedAt).format('MMMM Do YYYY, h:mm a')}
              </Typography>
            </Box>
            <Typography color="textSecondary">
              Exam Code: {examCode}
            </Typography>
          </Grid>
        </Grid>

        {/* Score Summary */}
        <Divider style={{ margin: '24px 0' }} />
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <Typography variant="h5">Score Summary</Typography>
            {aiEvaluation && (
              <Chip 
                icon={<ComputerIcon />}
                label="AI Evaluated" 
                color="primary" 
                style={{ marginLeft: 16 }}
              />
            )}
          </Box>
          <Box display="flex" alignItems="center">
            <Typography variant="h4" color="primary" style={{ marginRight: 8 }}>
              {scorePercentage}%
            </Typography>
            <Typography variant="body2" color="textSecondary">
              ({score}/{maxScore} points)
            </Typography>
          </Box>
        </Box>

        {/* AI Evaluation Button */}
        {!aiEvaluation && (
          <Box mt={2}>
            <Button
              variant="contained"
              color="primary"
              startIcon={evaluating ? <CircularProgress size={20} /> : <ComputerIcon />}
              onClick={handleAIEvaluation}
              disabled={evaluating}
            >
              {evaluating ? 'Evaluating...' : 'Run AI Evaluation'}
            </Button>
            <Paper 
              elevation={1} 
              style={{ 
                marginTop: 8, 
                padding: 12, 
                backgroundColor: '#e3f2fd',
                border: '1px solid #2196f3'
              }}
            >
              <Typography variant="body2" color="primary">
                AI evaluation will provide detailed feedback and scoring for text, code, and video questions.
              </Typography>
            </Paper>
          </Box>
        )}
      </Paper>

      {/* Questions and Answers */}
      {exam.sections.map((section, sIndex) => (
        <Paper key={section.id} elevation={1} style={{ marginBottom: 24 }}>
          <Box p={3} bgcolor="primary.main" color="white">
            <Typography variant="h6">
              Section {sIndex + 1}: {section.title}
            </Typography>
            <Typography variant="body2">
              Weightage: {section.weightage}%
            </Typography>
          </Box>
          
          <Box p={3}>
            {section.questions.map((question, qIndex) => (
              <Card key={question.id} variant="outlined" style={{ marginBottom: 16 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Typography variant="h6">
                      Question {qIndex + 1}
                    </Typography>
                    <Chip 
                      label={question.type.toUpperCase()} 
                      size="small" 
                      color="secondary"
                    />
                  </Box>
                  
                  <Typography variant="body1" gutterBottom>
                    {question.text}
                  </Typography>
                  
                  <Divider style={{ margin: '16px 0' }} />
                  
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Student's Answer:
                  </Typography>
                  
                  {renderAnswer(question, submission.answers[question.id])}
                  
                  {/* AI Evaluation Results */}
                  {getEvaluationDetails(question.id) && renderAIEvaluation(question.id)}
                </CardContent>
              </Card>
            ))}
          </Box>
        </Paper>
      ))}
    </div>
  );
};

export default SubmissionResult; 